import { useQueryMyOrders } from "@features/orders/hooks/useQueryMyOrders";
import { useQueryReceivedOrders } from "@features/orders/hooks/useQueryReceivedOrders";
import { useMarketplaceIdentity } from "@hooks/useMarketplaceIdentity";
import { useMemo } from "react";

/**
 * Whether the signed-in user has visible marketplace orders as customer or provider.
 * Matches the My Requests dashboard sections (My Orders + Orders Received).
 *
 * @param {Array<Record<string, unknown>>} myOrders
 * @param {Array<Record<string, unknown>>} receivedOrders
 * @param {{
 *   myTotal?: number,
 *   receivedTotal?: number,
 *   mySuccess?: boolean,
 *   receivedSuccess?: boolean,
 * }} [options]
 */
export function hasMyRequestsNavActivityFromOrders(
  myOrders,
  receivedOrders,
  {
    myTotal = 0,
    receivedTotal = 0,
    mySuccess = false,
    receivedSuccess = false,
  } = {}
) {
  if (myOrders.length > 0 || receivedOrders.length > 0) {
    return true;
  }

  if (mySuccess && myTotal > 0) {
    return true;
  }

  if (receivedSuccess && receivedTotal > 0) {
    return true;
  }

  return false;
}

/**
 * True when the signed-in user has visible marketplace orders as customer or provider.
 * Drives the top navbar "My Requests" link (profile menu always lists My Requests).
 */
export function useHasMyRequestsNavActivity() {
  const { sub, isAuthenticated } = useMarketplaceIdentity();

  const ordersEnabled = Boolean(isAuthenticated && sub);

  const {
    data: myOrders,
    total: myTotal,
    isSuccess: mySuccess,
  } = useQueryMyOrders({ enabled: ordersEnabled });
  const {
    data: receivedOrders,
    total: receivedTotal,
    isSuccess: receivedSuccess,
  } = useQueryReceivedOrders({ enabled: ordersEnabled });

  return useMemo(
    () =>
      hasMyRequestsNavActivityFromOrders(myOrders, receivedOrders, {
        myTotal,
        receivedTotal,
        mySuccess,
        receivedSuccess,
      }),
    [
      myOrders,
      receivedOrders,
      myTotal,
      receivedTotal,
      mySuccess,
      receivedSuccess,
    ]
  );
}
